import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  X,
  ShieldCheck,
  AlertCircle,
  Calendar,
  Infinity as InfinityIcon,
  Sprout,
  BarChart3,
  Megaphone,
  CalendarHeart,
  Users,
  Loader2,
  Plus,
  Trash2,
  Pencil,
} from 'lucide-react';
import { Card, CardTitle, CardDescription, Button, Skeleton, Input } from '@/components/ui';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import {
  ROLE_COLOR,
  ROLE_LABEL,
  STATUS_COLOR,
  STATUS_LABEL,
} from '@/lib/constants';
import { cn } from '@/lib/utils';
import { formatNumber } from '@keywordgod/shared';
import { useUserPrefs } from '@/lib/userPrefs';
import { useLocalStorageState } from '@/lib/useLocalStorageState';
import { Link } from 'react-router-dom';
import type {
  Announcement,
  AnnouncementLevel,
  MemberSummary,
  UserStatus,
  UserRole,
} from '@keywordgod/shared';

type Tab = 'seed' | 'usage' | 'announce' | 'season' | 'members';

export function Mod() {
  const user = useAuthStore((s) => s.user);
  const isStaff = !!user && (user.role === 'admin' || user.role === 'moderator');
  const isAdmin = user?.role === 'admin';

  if (!isStaff) {
    return (
      <Card className="mx-auto max-w-xl text-center border-red-500/40 bg-red-500/10">
        <AlertCircle className="mx-auto h-8 w-8 text-red-400" />
        <CardTitle className="mt-3 text-red-300">권한이 없습니다</CardTitle>
        <CardDescription className="mt-1">
          운영진(moderator) 이상만 접근할 수 있습니다.
        </CardDescription>
      </Card>
    );
  }

  const [tab, setTab] = useState<Tab>('seed');

  // 탭 정의 — moderator 는 회원 관리 제외
  const tabs: Array<{ id: Tab; label: string; icon: typeof Sprout; adminOnly?: boolean }> = [
    { id: 'seed', label: '키워드 시드', icon: Sprout },
    { id: 'usage', label: '사용 통계', icon: BarChart3 },
    { id: 'announce', label: '공지 발송', icon: Megaphone },
    { id: 'season', label: '시즌 키워드', icon: CalendarHeart },
    { id: 'members', label: '회원 관리', icon: Users, adminOnly: true },
  ];
  const visibleTabs = tabs.filter((t) => !t.adminOnly || isAdmin);
  // moderator 가 admin 전용 탭을 골랐을 때 fallback
  if (!visibleTabs.find((t) => t.id === tab)) {
    setTab(visibleTabs[0]!.id);
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-extrabold flex items-center gap-2">
          <ShieldCheck className="h-7 w-7 text-gold" />
          <span className="text-gold-gradient">운영자 콘솔</span>
        </h1>
        <p className="mt-1 text-sm text-gray-400">
          {isAdmin
            ? '관리자 — 회원 관리 + 운영 콘텐츠 모두 가능합니다.'
            : '부운영자 — 시드/통계/공지/시즌 4가지를 담당합니다. 회원 관리는 관리자에게 보고해주세요.'}
        </p>
      </header>

      <Card className="!p-0 overflow-hidden">
        <div className="flex flex-wrap border-b border-gray-800">
          {visibleTabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={cn(
                'inline-flex items-center gap-1.5 px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors',
                tab === t.id
                  ? 'border-gold text-gold'
                  : 'border-transparent text-gray-400 hover:text-white',
              )}
            >
              <t.icon className="h-4 w-4" />
              {t.label}
            </button>
          ))}
        </div>
        <div className="p-6">
          {tab === 'seed' && <SeedSection />}
          {tab === 'usage' && <UsageSection />}
          {tab === 'announce' && <AnnounceSection />}
          {tab === 'season' && <SeasonSection />}
          {tab === 'members' && isAdmin && <MembersSection />}
        </div>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// 1. 시드 투입
// ═══════════════════════════════════════════════════════════

function SeedSection() {
  const defaults = useQuery({
    queryKey: ['mod', 'seed-defaults'],
    queryFn: () => api.mod.seedDefaults(),
  });
  // Phase 55: localStorage 보존 — 탭 전환/이탈 후에도 입력 유지
  const [seedsText, setSeedsText] = useLocalStorageState('mod.seedText', '');
  const [depth, setDepth] = useState(1);
  const [maxKeywords, setMaxKeywords] = useState(5000);
  const [delayMs, setDelayMs] = useState(50);

  const run = useMutation({
    mutationFn: () => {
      const list = seedsText
        .split(/[\n,]/)
        .map((s) => s.trim())
        .filter(Boolean);
      return api.mod.seedRun({
        seeds: list.length > 0 ? list : undefined,
        depth,
        maxKeywords,
        delayMs,
      });
    },
    onError: (e: Error) => alert(`시드 실행 실패: ${e.message}`),
  });

  // Phase 49: 뉴스 자동 시드 수동 실행
  const newsRun = useMutation({
    mutationFn: () => api.mod.newsSeedRun(),
    onError: (e: Error) => alert(`뉴스 시드 실패: ${e.message}`),
  });

  // Phase 51: Gemini AI 자동 시드 생성
  const prefs = useUserPrefs();
  const [aiPerDomain, setAiPerDomain] = useState(500);
  const aiRun = useMutation({
    mutationFn: () =>
      api.mod.aiSeedRun({
        geminiApiKey: prefs.geminiApiKey,
        perDomain: aiPerDomain,
      }),
    onError: (e: Error) => alert(`AI 시드 실패: ${e.message}`),
  });

  function fillDefault() {
    if (!defaults.data) return;
    setSeedsText(defaults.data.seeds.slice(0, 30).join('\n'));
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-bold text-white flex items-center gap-2">
          <Sprout className="h-5 w-5 text-emerald-400" /> 키워드 시드 투입
        </h2>
        <p className="mt-1 text-sm text-gray-400">
          새 시드 키워드를 풀에 넣어 분석 풀을 확장합니다. BFS 자동 누적이 시드 → 연관어 → 분석 흐름을 이어갑니다.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <NumberField label="depth (1~2)" value={depth} onChange={setDepth} min={1} max={2} />
        <NumberField label="최대 키워드" value={maxKeywords} onChange={setMaxKeywords} min={100} max={50000} step={500} />
        <NumberField label="지연 ms" value={delayMs} onChange={setDelayMs} min={0} max={500} step={10} />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs text-gray-400">시드 (줄바꿈 또는 쉼표 구분, 비우면 기본 시드 사용)</label>
          <button
            type="button"
            onClick={fillDefault}
            disabled={!defaults.data}
            className="text-[11px] text-gold hover:underline"
          >
            기본 시드 30개 채우기
          </button>
        </div>
        <textarea
          rows={6}
          className="w-full rounded-lg border border-gray-800 bg-gray-950 px-3 py-2 text-sm text-white"
          placeholder="예: 다이어트&#10;홈트레이닝&#10;식단"
          value={seedsText}
          onChange={(e) => setSeedsText(e.target.value)}
        />
      </div>

      <div className="flex items-center gap-3">
        <Button variant="gold" onClick={() => run.mutate()} disabled={run.isPending}>
          {run.isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> 실행 중…
            </>
          ) : (
            <>
              <Sprout className="h-4 w-4" /> 시드 투입 시작
            </>
          )}
        </Button>
        {run.isPending && (
          <span className="text-xs text-gray-500">키워드 수에 따라 30초~5분 걸릴 수 있어요</span>
        )}
      </div>

      {run.data && (
        <Card className="border-emerald-400/30 bg-emerald-400/5">
          <div className="text-sm">
            <strong className="text-emerald-300">실행 완료</strong>
            <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <Stat label="시드 INSERT" value={run.data.seedsInserted} />
              <Stat label="총 INSERT" value={run.data.totalInserted} />
              <Stat label="API 호출" value={run.data.apiCalls} />
              <Stat label="에러" value={run.data.errors} tone={run.data.errors > 0 ? 'red' : 'gray'} />
            </div>
            <div className="mt-2 text-[11px] text-gray-500">
              {(run.data.durationMs / 1000).toFixed(1)}초 · depth {run.data.depthReached}
              {run.data.capHit && ' · 최대 한도 도달'}
            </div>
          </div>
        </Card>
      )}

      {/* Phase 51: Gemini AI 자동 시드 (가장 강력) */}
      <div className="border-t border-gray-800 pt-5">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <h3 className="text-base font-bold text-white flex items-center gap-2">
            🤖 AI 자동 시드 생성
            <span className="rounded-full bg-purple-500/20 px-2 py-0.5 text-[10px] font-bold text-purple-300">
              Gemini · 도메인×{aiPerDomain} ≈ {(aiPerDomain * 10).toLocaleString()}개
            </span>
          </h3>
          <div className="flex items-center gap-2">
            <select
              value={aiPerDomain}
              onChange={(e) => setAiPerDomain(Number(e.target.value))}
              className="h-9 rounded-lg border border-gray-800 bg-gray-950 px-2 text-xs text-white"
              disabled={aiRun.isPending}
            >
              <option value={200}>200/도메인 (~2K)</option>
              <option value={300}>300/도메인 (~3K)</option>
              <option value={500}>500/도메인 (~5K)</option>
              <option value={800}>800/도메인 (~8K)</option>
            </select>
            <Button
              variant="gold"
              onClick={() => {
                if (!prefs.geminiApiKey) {
                  alert('먼저 설정 페이지에서 Gemini API 키를 저장해주세요.');
                  return;
                }
                aiRun.mutate();
              }}
              disabled={aiRun.isPending}
            >
              {aiRun.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> AI 생성 중…
                </>
              ) : (
                'AI 시드 생성'
              )}
            </Button>
          </div>
        </div>
        <p className="text-xs text-gray-400 leading-snug">
          Gemini 가 10개 정책/시사/세금/복지/노인/육아/교육/일자리/부동산/경제 도메인별로 키워드를 자동 생성 →
          INSERT OR IGNORE 시드 등록. <strong>사용자 본인 Gemini 키 사용</strong> (서버 quota 0).
          {!prefs.geminiApiKey && (
            <>
              {' '}
              <Link to="/settings" className="text-gold hover:underline">
                먼저 설정에서 키 저장하기 →
              </Link>
            </>
          )}
        </p>
        {aiRun.isPending && (
          <p className="mt-2 text-[11px] text-purple-300">
            10개 도메인 × Gemini 호출 → 30~90초 걸립니다. 이 페이지 닫지 마세요.
          </p>
        )}
        {aiRun.data && (
          <Card className="mt-3 border-purple-400/30 bg-purple-400/5">
            <div className="text-sm">
              <strong className="text-purple-300">AI 시드 완료</strong>
              <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <Stat label="도메인 성공" value={aiRun.data.domainsProcessed} />
                <Stat
                  label="실패"
                  value={aiRun.data.domainsFailed}
                  tone={aiRun.data.domainsFailed > 0 ? 'red' : 'gray'}
                />
                <Stat label="추출 후보" value={aiRun.data.candidatesExtracted} />
                <Stat label="신규 INSERT" value={aiRun.data.inserted} />
              </div>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-1.5 text-[11px]">
                {aiRun.data.perDomain.map((d) => (
                  <div
                    key={d.id}
                    className={cn(
                      'rounded-md border px-2 py-1 flex items-center justify-between',
                      d.error
                        ? 'border-red-500/40 bg-red-500/5 text-red-200'
                        : 'border-gray-800 bg-gray-950 text-gray-300',
                    )}
                  >
                    <span className="truncate">{d.label}</span>
                    {d.error ? (
                      <span className="text-[10px]">실패</span>
                    ) : (
                      <span className="tabular font-bold text-purple-300">+{d.got}</span>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-2 text-[11px] text-gray-500">
                {(aiRun.data.durationMs / 1000).toFixed(1)}초 — 분석은 cron 이 점진 처리 (~수십분~수시간)
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Phase 49: 뉴스 자동 시드 */}
      <div className="border-t border-gray-800 pt-5">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <h3 className="text-base font-bold text-white flex items-center gap-2">
            📰 뉴스 자동 시드
            <span className="rounded-full bg-blue-500/15 px-2 py-0.5 text-[10px] font-bold text-blue-300">
              매일 새벽 03:00 자동
            </span>
          </h3>
          <Button
            variant="secondary"
            onClick={() => newsRun.mutate()}
            disabled={newsRun.isPending}
          >
            {newsRun.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> 뉴스 수집 중…
              </>
            ) : (
              '지금 실행'
            )}
          </Button>
        </div>
        <p className="text-xs text-gray-400">
          네이버 뉴스에서 시사 헤드라인을 가져와 명사구를 자동 추출 → 시드로 등록합니다.
          쇼핑 편중을 막고 정책/지원금/경제 키워드를 매일 보충합니다.
        </p>
        {newsRun.data && (
          <Card className="mt-3 border-blue-400/30 bg-blue-400/5">
            <div className="text-sm">
              <strong className="text-blue-300">뉴스 시드 완료</strong>
              <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <Stat label="쿼리" value={newsRun.data.queriesProcessed} />
                <Stat label="헤드라인" value={newsRun.data.headlinesFetched} />
                <Stat label="추출 후보" value={newsRun.data.candidatesExtracted} />
                <Stat label="신규 INSERT" value={newsRun.data.inserted} />
              </div>
              <div className="mt-2 text-[11px] text-gray-500">
                {(newsRun.data.durationMs / 1000).toFixed(1)}초
                {newsRun.data.errors > 0 && ` · 에러 ${newsRun.data.errors}`}
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// 2. 사용 통계
// ═══════════════════════════════════════════════════════════

function UsageSection() {
  const q = useQuery({
    queryKey: ['mod', 'usage-stats'],
    queryFn: () => api.mod.usageStats(200),
  });

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-bold text-white flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-blue-300" /> 회원별 사용 통계
        </h2>
        <p className="mt-1 text-sm text-gray-400">
          어떤 회원이 어떤 도메인을 분석하는지 보고 키워드 풀 확장 방향을 결정합니다.
        </p>
      </div>

      {q.isLoading && <Skeleton className="h-40 w-full" />}
      {q.isError && <p className="text-sm text-red-400">{(q.error as Error).message}</p>}
      {q.data && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b border-gray-800">
                <th className="py-2 pr-2 font-medium">회원</th>
                <th className="py-2 pr-2 font-medium text-right">최근 7일</th>
                <th className="py-2 pr-2 font-medium text-right">최근 30일</th>
                <th className="py-2 pr-2 font-medium text-right">총 검색</th>
                <th className="py-2 font-medium">마지막 검색</th>
              </tr>
            </thead>
            <tbody>
              {q.data.map((r) => (
                <tr key={r.userId} className="border-b border-gray-900 hover:bg-gray-950/60">
                  <td className="py-2 pr-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-white">
                        {r.nickname || <span className="text-gray-500 italic">(미입력)</span>}
                      </span>
                      <span className="text-[10px] text-gray-500">{r.email}</span>
                      <span className={cn('rounded-full px-1.5 py-0.5 text-[10px] font-bold', ROLE_COLOR[r.role])}>
                        {ROLE_LABEL[r.role]}
                      </span>
                    </div>
                  </td>
                  <td className="py-2 pr-2 text-right tabular text-blue-300 font-bold">
                    {formatNumber(r.last7d)}
                  </td>
                  <td className="py-2 pr-2 text-right tabular">{formatNumber(r.last30d)}</td>
                  <td className="py-2 pr-2 text-right tabular text-gray-300">
                    {formatNumber(r.totalSearches)}
                  </td>
                  <td className="py-2 text-xs text-gray-500">
                    {r.lastSearchAt ? new Date(r.lastSearchAt * 1000).toLocaleString('ko-KR') : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// 3. 공지사항
// ═══════════════════════════════════════════════════════════

function AnnounceSection() {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['mod', 'announcements'],
    queryFn: () => api.mod.listAnnouncements(),
  });

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [level, setLevel] = useState<AnnouncementLevel>('info');
  const [endsDate, setEndsDate] = useState('');

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['mod', 'announcements'] });
    qc.invalidateQueries({ queryKey: ['announcements', 'active'] });
  }

  const create = useMutation({
    mutationFn: () => {
      const endsAt = endsDate
        ? Math.floor(new Date(endsDate + 'T23:59:59').getTime() / 1000)
        : null;
      return api.mod.createAnnouncement({
        title: title.trim(),
        body: body.trim(),
        level,
        endsAt,
      });
    },
    onSuccess: () => {
      setTitle('');
      setBody('');
      setEndsDate('');
      invalidate();
    },
    onError: (e: Error) => alert(`공지 작성 실패: ${e.message}`),
  });

  const toggleEnabled = useMutation({
    mutationFn: (p: { id: number; enabled: boolean }) =>
      api.mod.updateAnnouncement(p.id, { enabled: p.enabled }),
    onSuccess: invalidate,
  });
  const del = useMutation({
    mutationFn: (id: number) => api.mod.deleteAnnouncement(id),
    onSuccess: invalidate,
  });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-white flex items-center gap-2">
          <Megaphone className="h-5 w-5 text-amber-300" /> 공지 발송
        </h2>
        <p className="mt-1 text-sm text-gray-400">
          모든 로그인 회원에게 상단 배너로 노출됩니다. 점검 / 이벤트 / 팁 공유에 사용하세요.
        </p>
      </div>

      {/* 작성 폼 */}
      <Card className="border-amber-400/30 bg-amber-400/5">
        <CardTitle className="text-sm">새 공지 작성</CardTitle>
        <div className="mt-3 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={level}
              onChange={(e) => setLevel(e.target.value as AnnouncementLevel)}
              className="h-9 rounded-lg border border-gray-800 bg-gray-950 px-2 text-sm text-white"
            >
              <option value="info">정보 (파랑)</option>
              <option value="event">이벤트 (보라)</option>
              <option value="warn">주의 (주황)</option>
            </select>
            <label className="text-xs text-gray-400">종료일:</label>
            <input
              type="date"
              value={endsDate}
              onChange={(e) => setEndsDate(e.target.value)}
              className="h-9 rounded-lg border border-gray-800 bg-gray-950 px-2 text-sm text-white"
            />
            {!endsDate && <span className="text-[11px] text-gray-500">미지정 = 무기한</span>}
          </div>
          <Input
            placeholder="제목"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <textarea
            rows={3}
            placeholder="본문 (한 줄 ~ 두세 줄 권장)"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="w-full rounded-lg border border-gray-800 bg-gray-950 px-3 py-2 text-sm text-white"
          />
          <Button
            variant="gold"
            disabled={!title.trim() || !body.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            게시
          </Button>
        </div>
      </Card>

      {/* 목록 */}
      <div>
        <div className="text-xs uppercase tracking-widest text-gray-500 mb-2">전체 공지</div>
        {list.isLoading && <Skeleton className="h-32 w-full" />}
        {list.data && list.data.length === 0 && (
          <p className="text-sm text-gray-500">아직 공지가 없습니다</p>
        )}
        <div className="space-y-2">
          {list.data?.map((a) => (
            <AnnouncementRow
              key={a.id}
              a={a}
              onToggle={() => toggleEnabled.mutate({ id: a.id, enabled: !a.enabled })}
              onDelete={() => {
                if (confirm(`"${a.title}" 공지를 삭제할까요?`)) del.mutate(a.id);
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function AnnouncementRow({
  a,
  onToggle,
  onDelete,
}: {
  a: Announcement;
  onToggle: () => void;
  onDelete: () => void;
}) {
  const levelClass = {
    info: 'border-blue-400/30 bg-blue-400/5 text-blue-200',
    event: 'border-purple-400/30 bg-purple-400/5 text-purple-200',
    warn: 'border-amber-400/40 bg-amber-400/10 text-amber-200',
  }[a.level];
  return (
    <div className={cn('rounded-lg border p-3', levelClass, !a.enabled && 'opacity-50')}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <strong className="text-white">{a.title}</strong>
            <span className="text-[10px] uppercase tracking-widest opacity-70">{a.level}</span>
            {!a.enabled && (
              <span className="rounded-full bg-gray-800 px-2 py-0.5 text-[10px] font-bold text-gray-400">
                비활성
              </span>
            )}
          </div>
          <p className="mt-1 text-xs whitespace-pre-wrap">{a.body}</p>
          <div className="mt-1.5 text-[10px] text-gray-500">
            작성 {new Date(a.createdAt * 1000).toLocaleString('ko-KR')}
            {a.endsAt ? ` · 종료 ${new Date(a.endsAt * 1000).toLocaleDateString('ko-KR')}` : ' · 무기한'}
            {a.authorNickname && ` · ${a.authorNickname}`}
          </div>
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          <button
            type="button"
            onClick={onToggle}
            className="rounded-md border border-gray-700 bg-gray-900 px-2 py-1 text-[10px] text-gray-200 hover:bg-gray-800"
          >
            {a.enabled ? '비활성화' : '활성화'}
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="rounded-md border border-red-500/30 bg-red-500/10 px-2 py-1 text-[10px] text-red-300 hover:bg-red-500/20"
          >
            <Trash2 className="h-3 w-3 inline" /> 삭제
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// 4. 시즌 키워드 편집
// ═══════════════════════════════════════════════════════════

function SeasonSection() {
  const qc = useQueryClient();
  // 기존 공개 API 로 목록 조회 (모든 이벤트)
  const list = useQuery({
    queryKey: ['season', 'all'],
    queryFn: () => api.season.all(),
  });

  const [form, setForm] = useState({
    category: 'holiday',
    categoryLabel: '명절',
    name: '',
    description: '',
    emoji: '',
    startsMd: '',
    endsMd: '',
    keywords: '',
    recurring: true,
  });

  const create = useMutation({
    mutationFn: () =>
      api.mod.createSeasonEvent({
        category: form.category.trim(),
        categoryLabel: form.categoryLabel.trim(),
        name: form.name.trim(),
        description: form.description.trim() || null,
        emoji: form.emoji.trim() || null,
        startsMd: form.startsMd.trim(),
        endsMd: form.endsMd.trim() || null,
        keywords: form.keywords
          .split(/[\n,]/)
          .map((s) => s.trim())
          .filter(Boolean),
        recurring: form.recurring,
      }),
    onSuccess: () => {
      setForm({
        category: 'holiday',
        categoryLabel: '명절',
        name: '',
        description: '',
        emoji: '',
        startsMd: '',
        endsMd: '',
        keywords: '',
        recurring: true,
      });
      qc.invalidateQueries({ queryKey: ['season'] });
    },
    onError: (e: Error) => alert(`시즌 이벤트 추가 실패: ${e.message}`),
  });

  const del = useMutation({
    mutationFn: (id: number) => api.mod.deleteSeasonEvent(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['season'] }),
  });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-white flex items-center gap-2">
          <CalendarHeart className="h-5 w-5 text-pink-300" /> 시즌 키워드 편집
        </h2>
        <p className="mt-1 text-sm text-gray-400">
          다가오는 시즌 이벤트(명절·기념일·이벤트)를 카탈로그에 추가/편집합니다. 회원 대시보드의 시즌 카드와 D-NN 알림에 사용됩니다.
        </p>
      </div>

      {/* 추가 폼 */}
      <Card className="border-pink-400/30 bg-pink-400/5">
        <CardTitle className="text-sm">새 시즌 이벤트 추가</CardTitle>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input placeholder="카테고리 id (예: holiday)" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
          <Input placeholder="카테고리 라벨 (예: 명절)" value={form.categoryLabel} onChange={(e) => setForm({ ...form, categoryLabel: e.target.value })} />
          <Input placeholder="이벤트 이름 (예: 추석)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input placeholder="이모지 (예: 🌕)" value={form.emoji} onChange={(e) => setForm({ ...form, emoji: e.target.value })} />
          <Input placeholder="시작 MM-DD (예: 09-29)" value={form.startsMd} onChange={(e) => setForm({ ...form, startsMd: e.target.value })} />
          <Input placeholder="종료 MM-DD (선택)" value={form.endsMd} onChange={(e) => setForm({ ...form, endsMd: e.target.value })} />
          <div className="md:col-span-2">
            <Input placeholder="설명 (선택)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <textarea
              rows={3}
              placeholder="키워드 (줄바꿈 또는 쉼표 구분)&#10;예: 추석 선물&#10;추석 음식"
              value={form.keywords}
              onChange={(e) => setForm({ ...form, keywords: e.target.value })}
              className="w-full rounded-lg border border-gray-800 bg-gray-950 px-3 py-2 text-sm text-white"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input
              type="checkbox"
              checked={form.recurring}
              onChange={(e) => setForm({ ...form, recurring: e.target.checked })}
            />
            매년 반복 (해제 시 단발성 이벤트로 처리)
          </label>
        </div>
        <div className="mt-3">
          <Button
            variant="gold"
            disabled={
              !form.category.trim() ||
              !form.categoryLabel.trim() ||
              !form.name.trim() ||
              !/^\d{2}-\d{2}$/.test(form.startsMd) ||
              create.isPending
            }
            onClick={() => create.mutate()}
          >
            {create.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            추가
          </Button>
        </div>
      </Card>

      {/* 목록 */}
      <div>
        <div className="text-xs uppercase tracking-widest text-gray-500 mb-2">
          현재 등록된 이벤트 {list.data?.length ?? 0}개
        </div>
        {list.isLoading && <Skeleton className="h-32 w-full" />}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {list.data?.map((e) => (
            <div key={e.id} className="rounded-lg border border-gray-800 bg-gray-950 p-3 flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span>{e.emoji}</span>
                  <strong className="text-white truncate">{e.name}</strong>
                  <span className="text-[10px] text-gray-500">· {e.categoryLabel}</span>
                </div>
                <div className="text-[11px] text-gray-400 mt-0.5">
                  {e.startsMd}
                  {e.endsMd ? ` ~ ${e.endsMd}` : ''} · {e.recurring ? '매년' : '단발'} · 키워드 {e.keywords.length}개
                </div>
                {e.description && (
                  <div className="text-[10px] text-gray-500 mt-0.5 truncate">{e.description}</div>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  if (confirm(`"${e.name}" 이벤트를 삭제할까요?`)) del.mutate(e.id);
                }}
                className="text-[10px] text-red-300 hover:underline shrink-0"
              >
                <Trash2 className="h-3 w-3 inline" /> 삭제
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// 5. 회원 관리 (admin only)
// ═══════════════════════════════════════════════════════════

type MemberTab = 'pending' | 'active' | 'suspended';

function MembersSection() {
  const [tab, setTab] = useState<MemberTab>('pending');

  const stats = useQuery({ queryKey: ['mod', 'stats'], queryFn: () => api.mod.stats() });
  const s = stats.data;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <StatBox label="가입 대기" value={s?.pending} highlight={!!s && s.pending > 0} />
        <StatBox label="활성" value={s?.active} />
        <StatBox label="정지" value={s?.suspended} />
        <StatBox label="운영진" value={s?.moderators} />
        <StatBox label="관리자" value={s?.admins} />
      </div>

      <div className="flex items-center gap-2 border-b border-gray-800">
        {(['pending', 'active', 'suspended'] as MemberTab[]).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={cn(
              'px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors',
              tab === t
                ? 'border-gold text-gold'
                : 'border-transparent text-gray-400 hover:text-white',
            )}
          >
            {STATUS_LABEL[t]}
          </button>
        ))}
      </div>
      <MembersList status={tab} />
    </div>
  );
}

function MembersList({ status }: { status: MemberTab }) {
  const me = useAuthStore((s) => s.user);
  const qc = useQueryClient();

  const q = useQuery({
    queryKey:
      status === 'pending' ? ['mod', 'pending'] : ['mod', 'members', status],
    queryFn: () =>
      status === 'pending'
        ? api.mod.pending()
        : api.mod.members({ status: status as UserStatus }),
  });

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['mod', 'pending'] });
    qc.invalidateQueries({ queryKey: ['mod', 'members'] });
    qc.invalidateQueries({ queryKey: ['mod', 'stats'] });
  }

  const approve = useMutation({ mutationFn: api.mod.approve, onSuccess: invalidate });
  const reject = useMutation({ mutationFn: api.mod.reject, onSuccess: invalidate });
  const suspend = useMutation({ mutationFn: api.mod.suspend, onSuccess: invalidate });
  const restore = useMutation({ mutationFn: api.mod.restore, onSuccess: invalidate });
  const setRole = useMutation({
    mutationFn: (p: { id: string; role: UserRole }) => api.mod.setRole(p.id, p.role),
    onSuccess: invalidate,
    onError: (e: Error) => alert(`역할 변경 실패: ${e.message}`),
  });
  const setExpires = useMutation({
    mutationFn: (p: { id: string; expiresAt: number | null }) =>
      api.mod.setExpires(p.id, p.expiresAt),
    onSuccess: invalidate,
    onError: (e: Error) => alert(`기간 설정 실패: ${e.message}`),
  });

  if (q.isLoading) return <Skeleton className="h-40 w-full" />;
  if (q.isError) return <p className="text-sm text-red-400">{(q.error as Error).message}</p>;
  const list = q.data ?? [];
  if (list.length === 0) {
    return <p className="text-sm text-gray-500">{status === 'pending' ? '승인 대기 회원이 없습니다 🎉' : '회원이 없습니다'}</p>;
  }

  return (
    <ul className="divide-y divide-gray-800">
      {list.map((m) => (
        <li key={m.id} className="py-4 flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <Avatar src={m.picture} name={m.nickname || m.name} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-white truncate">
                  {m.nickname || <span className="text-gray-500 italic">(닉네임 미입력)</span>}
                </span>
                <span className="text-[11px] text-gray-500 truncate">· {m.name}</span>
                <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-bold', ROLE_COLOR[m.role])}>
                  {ROLE_LABEL[m.role]}
                </span>
                <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-bold', STATUS_COLOR[m.status])}>
                  {STATUS_LABEL[m.status]}
                </span>
                <ExpiryBadge expiresAt={m.expiresAt} />
              </div>
              <div className="text-xs text-gray-400 truncate">{m.email}</div>
              <div className="text-[10px] text-gray-600 mt-0.5">
                가입 {fmtDate(m.createdAt)}
                {m.approvedAt ? ` · 승인 ${fmtDate(m.approvedAt)}` : ''}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 md:justify-end">
            {m.status === 'active' && (
              <>
                {m.id !== me?.id && (
                  <select
                    className="h-9 rounded-lg border border-gray-800 bg-gray-900 px-2 text-xs text-white"
                    value={m.role}
                    onChange={(e) =>
                      setRole.mutate({ id: m.id, role: e.target.value as UserRole })
                    }
                    disabled={setRole.isPending}
                  >
                    <option value="member">일반</option>
                    <option value="moderator">부운영자</option>
                    <option value="admin">관리자</option>
                  </select>
                )}
                <ExpiryPicker
                  current={m.expiresAt ?? null}
                  onChange={(ts) => setExpires.mutate({ id: m.id, expiresAt: ts })}
                  disabled={setExpires.isPending}
                />
              </>
            )}
            <MemberActions
              member={m}
              myId={me?.id}
              onApprove={() => approve.mutate(m.id)}
              onReject={() => reject.mutate(m.id)}
              onSuspend={() => suspend.mutate(m.id)}
              onRestore={() => restore.mutate(m.id)}
              busy={approve.isPending || reject.isPending || suspend.isPending || restore.isPending}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

function MemberActions({
  member,
  myId,
  onApprove,
  onReject,
  onSuspend,
  onRestore,
  busy,
}: {
  member: MemberSummary;
  myId?: string;
  onApprove: () => void;
  onReject: () => void;
  onSuspend: () => void;
  onRestore: () => void;
  busy?: boolean;
}) {
  const isSelf = member.id === myId;
  const isAdmin = member.role === 'admin';

  if (member.status === 'pending') {
    return (
      <div className="flex gap-2 shrink-0">
        <Button size="sm" variant="gold" onClick={onApprove} disabled={busy}>
          <Check className="h-4 w-4" /> 승인
        </Button>
        <Button size="sm" variant="secondary" onClick={onReject} disabled={busy}>
          <X className="h-4 w-4" /> 거절
        </Button>
      </div>
    );
  }
  if (member.status === 'active') {
    if (isSelf || isAdmin) {
      return <span className="text-xs text-gray-500 shrink-0">정지 불가</span>;
    }
    return (
      <Button size="sm" variant="danger" onClick={onSuspend} disabled={busy}>
        정지
      </Button>
    );
  }
  return (
    <Button size="sm" variant="secondary" onClick={onRestore} disabled={busy}>
      복구
    </Button>
  );
}

// ═══════════════════════════════════════════════════════════
// 공용 UI
// ═══════════════════════════════════════════════════════════

function NumberField({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
}) {
  return (
    <label className="block">
      <span className="text-xs text-gray-400">{label}</span>
      <input
        type="number"
        className="mt-1 w-full h-10 rounded-lg border border-gray-800 bg-gray-950 px-3 text-sm text-white"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        min={min}
        max={max}
        step={step}
      />
    </label>
  );
}

function Stat({ label, value, tone = 'gray' }: { label: string; value: number; tone?: 'gray' | 'red' }) {
  const cls = tone === 'red' ? 'text-red-300' : 'text-white';
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-gray-500">{label}</div>
      <div className={cn('tabular text-xl font-extrabold mt-0.5', cls)}>{formatNumber(value)}</div>
    </div>
  );
}

function StatBox({ label, value, highlight }: { label: string; value: number | undefined; highlight?: boolean }) {
  return (
    <div className={cn('rounded-xl border border-gray-800 bg-gray-950 px-3 py-3', highlight && 'border-amber-400/50 bg-amber-400/5')}>
      <div className="text-[10px] uppercase tracking-widest text-gray-500">{label}</div>
      <div className="tabular text-2xl font-extrabold text-white mt-1">{value ?? '-'}</div>
    </div>
  );
}

function Avatar({ src, name }: { src?: string; name: string }) {
  if (src) {
    return (
      <img
        src={src}
        alt={name}
        className="h-10 w-10 rounded-full ring-1 ring-gray-700 object-cover shrink-0"
        referrerPolicy="no-referrer"
      />
    );
  }
  return (
    <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#FFD700] to-[#FFA500] text-black text-sm font-bold flex items-center justify-center shrink-0">
      {name.slice(0, 1).toUpperCase()}
    </div>
  );
}

function ExpiryBadge({ expiresAt }: { expiresAt?: number | null }) {
  if (!expiresAt) {
    return (
      <span className="inline-flex items-center gap-0.5 rounded-full bg-gray-800 px-2 py-0.5 text-[10px] font-medium text-gray-400">
        <InfinityIcon className="h-2.5 w-2.5" /> 무기한
      </span>
    );
  }
  const now = Math.floor(Date.now() / 1000);
  const diff = expiresAt - now;
  const days = Math.floor(diff / 86400);
  const expired = diff <= 0;
  let cls = 'bg-emerald-500/15 text-emerald-300';
  let label = `D-${days}`;
  if (expired) {
    cls = 'bg-red-500/20 text-red-300';
    label = '만료됨';
  } else if (days <= 7) {
    cls = 'bg-amber-500/20 text-amber-300';
  }
  return (
    <span
      className={cn('inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[10px] font-bold', cls)}
      title={`만료: ${fmtDate(expiresAt)}`}
    >
      <Calendar className="h-2.5 w-2.5" /> {label}
    </span>
  );
}

function ExpiryPicker({
  current,
  onChange,
  disabled,
}: {
  current: number | null;
  onChange: (epochS: number | null) => void;
  disabled?: boolean;
}) {
  const presets: Array<{ label: string; days: number | 'none' }> = [
    { label: '+7일', days: 7 },
    { label: '+30일', days: 30 },
    { label: '+90일', days: 90 },
    { label: '+1년', days: 365 },
    { label: '무기한', days: 'none' },
  ];
  const [open, setOpen] = useState(false);
  const [customDate, setCustomDate] = useState('');

  function applyPreset(d: number | 'none') {
    if (d === 'none') {
      onChange(null);
    } else {
      const ts = Math.floor(Date.now() / 1000) + d * 86400;
      onChange(ts);
    }
    setOpen(false);
  }
  function applyCustom() {
    if (!customDate) return;
    const d = new Date(customDate + 'T23:59:59');
    if (Number.isNaN(d.getTime())) return;
    onChange(Math.floor(d.getTime() / 1000));
    setOpen(false);
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        className="h-9 inline-flex items-center gap-1.5 rounded-lg border border-gold/30 bg-gold/5 px-2.5 text-xs font-semibold text-gold hover:bg-gold/10 disabled:opacity-50"
      >
        <Calendar className="h-3.5 w-3.5" /> 기간
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-56 z-30 rounded-xl border border-gray-800 bg-gray-900 shadow-2xl p-3">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-2">
            현재: {current ? fmtDate(current) : '무기한'}
          </div>
          <div className="grid grid-cols-2 gap-1.5 mb-3">
            {presets.map((p) => (
              <button
                key={p.label}
                type="button"
                onClick={() => applyPreset(p.days)}
                className="rounded-md border border-gray-800 bg-gray-950 px-2 py-1.5 text-xs text-gray-200 hover:border-gold/40 hover:text-gold"
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="border-t border-gray-800 pt-2">
            <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">직접 입력</div>
            <div className="flex items-center gap-1">
              <input
                type="date"
                value={customDate}
                onChange={(e) => setCustomDate(e.target.value)}
                className="flex-1 h-8 rounded-md border border-gray-800 bg-gray-950 px-2 text-xs text-white"
              />
              <button
                type="button"
                onClick={applyCustom}
                className="h-8 rounded-md bg-gold/15 px-2 text-xs font-bold text-gold hover:bg-gold/25"
              >
                적용
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function fmtDate(unix: number): string {
  return new Date(unix * 1000).toLocaleDateString('ko-KR');
}
